<template>
  <v-row justify="center">
    <v-dialog v-model="open" width="640px" scrollable>
      <v-card class="">
        <!-- close -->
        <v-toolbar elevation="0" class="title pl-2">
          <v-btn icon @click="backToList" v-if="isEditing || isCreating">
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
          <span class="roleTitle">
            {{
              isCreating
                ? $t('message.pages.users.permissionsEdit.newPermission')
                : isEditing
                ? $t('message.pages.users.permissionsEdit.editPermission')
                : $t('message.pages.users.actions.permissions')
            }}
          </span>
          <v-progress-circular
            v-if="isRemoving"
            class="ml-2 pb-2"
            :size="18"
            width="3"
            color="primary"
            indeterminate
          />
          <v-spacer />
          <v-toolbar-items>
            <v-btn icon @click="handleClose">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>

        <!-- steps -->
        <v-card-text
          class="my-0 py-0 px-6"
          :style="isCreating || isEditing ? 'height: 465px' : 'height: 500px'"
        >
          <v-row
            no-gutters
            v-if="!isEditing && !isCreating"
            class="mb-16 pb-16"
          >
            <v-col cols="12" class="mb-6 mt-2">
              <v-btn
                color="primary"
                class="elevation-0"
                @click="handleCreatePermission"
              >
                {{ $t('message.pages.users.permissionsEdit.newPermission') }}
              </v-btn>
            </v-col>
            <v-card
              v-for="(role, i) in roles"
              :key="i"
              class="px-4 mb-4 roleCard d-flex align-center justify-center"
              width="100%"
              color="newBackground"
              height="102px"
              flat
            >
              <v-row no-gutters>
                <v-col cols="10" class="d-flex align-center justify-center">
                  <v-row no-gutters>
                    <v-col cols="12">
                      <span class="roleTitle" v-if="role.name">
                        {{ capitalizeFirstLetter(role.name) }}
                        <br />
                        <span
                          v-if="role.description"
                          :title="role.description"
                          class="roleSubtitle textType--text"
                          >{{ role.description }}</span
                        >
                      </span>
                    </v-col>
                  </v-row>
                </v-col>
                <v-col
                  v-if="role.custom"
                  cols="2"
                  class="d-flex align-center justify-center"
                  style="max-height: 90px"
                >
                  <v-row no-gutters>
                    <v-btn
                      icon
                      @click="handleEditRole(role)"
                      class="mt-1"
                      :disabled="isRemoving"
                    >
                      <v-icon color="primary">mdi-pencil</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      @click="handleDeleteRole(role)"
                      class="mt-1"
                      :disabled="isRemoving"
                    >
                      <v-icon color="error">mdi-delete</v-icon>
                    </v-btn>
                  </v-row>
                </v-col>
              </v-row>
            </v-card>
          </v-row>
          <v-row no-gutters v-else>
            <v-col cols="12">
              <span class="permissionFieldTitle">{{ $t('message.name') }}</span>
            </v-col>
            <v-col cols="5" class="mt-1">
              <v-text-field
                v-model="roleData.name"
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="12">
              <span class="permissionFieldTitle">{{
                $t('message.description')
              }}</span>
            </v-col>
            <v-col cols="12" class="mt-1">
              <v-text-field
                v-model="roleData.description"
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="12" v-if="errors.unknownError">
              <v-card flat height="30">
                <span class="red--text footerText align-text d-block my-1">
                  <span>
                    {{ $t('message.errors.unexpectedError') }}
                    <v-tooltip top>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon size="18" v-bind="attrs" v-on="on">
                          mdi-help-circle
                        </v-icon>
                      </template>
                      <v-container
                        class="text-body-2 pa-0"
                        style="max-width: 230px"
                      >
                        {{ errors.errMsg }}
                      </v-container>
                    </v-tooltip>
                  </span>
                </span>
              </v-card>
            </v-col>
            <v-col cols="12">
              <v-card flat outlined class="pa-6 roleCard" color="newBackground">
                <v-row no-gutters>
                  <v-col
                    cols="12"
                    v-for="(permission, i) in permissionsTree"
                    :key="i"
                  >
                    <v-row no-gutters justify="space-between" align="center">
                      <span
                        class="permissionFieldTitle"
                        v-if="permission.renamedGroup"
                      >
                        {{ capitalizeFirstLetter(permission.renamedGroup) }}
                      </span>
                      <v-select
                        v-model="permission.switch"
                        :items="permission.selectValues"
                        item-text="title"
                        item-value="value"
                        dense
                        style="max-width: 165px"
                        outlined
                        hide-details
                      ></v-select>
                      <v-col
                        cols="12"
                        class="py-4"
                        v-if="permissionsTree.length - 1 != i"
                      >
                        <v-divider />
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
        <!-- actions -->
        <v-card-actions class="px-6 py-6">
          <v-row
            no-gutters
            justify="center"
            align="center"
            v-if="isEditing || isCreating"
          >
            <v-spacer />
            <v-btn
              color="primary"
              class="ml-2 mr-2"
              :loading="isLoadingUpdate"
              :disabled="!canSaveRole"
              @click="handleSaveRole"
            >
              {{ $t('message.actions.save') }}
            </v-btn>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import organizationApi from '@/services/organizationApi'

export default {
  name: 'PermissionEdit',
  components: {},
  data: () => ({
    open: false,
    roleData: {
      name: '',
      description: '',
      permissions: [],
    },
    users: [],
    roles: [],
    permissionsTree: [],
    defaultPermissionsTree: [],
    isCreating: false,
    isEditing: false,
    editingRoleId: '',
    isLoadingUpdate: false,
    isRemoving: false,
    errors: {
      unknownError: false,
      errMsg: '',
    },
  }),
  computed: {
    canSaveRole() {
      return (
        this.roleData.name.length > 2 && this.roleData.description.length > 2
      )
    },
  },
  mounted() {
    this.$root.$on('openPermissionEdit', (data) => {
      Object.assign(this.$data, this.initialState())
      this.users = data.users
      this.roles = data.roles
      this.permissionsTree = JSON.parse(JSON.stringify(data.permissions))
      this.defaultPermissionsTree = JSON.parse(JSON.stringify(data.permissions))
      this.open = true
      this.orderRolesByName()
    })
  },
  beforeDestroy() {
    this.$root.$off('openPermissionEdit')
  },
  methods: {
    capitalizeFirstLetter(val) {
      return val.charAt(0).toUpperCase() + val.slice(1)
    },
    handleSaveRole() {
      if (this.isCreating) {
        this.handleCreatePermissions()
      }
      if (this.isEditing) {
        this.handleUpdateRole()
      }
    },
    orderRolesByName() {
      this.roles
        .filter((role) => !role.custom)
        .sort((a, b) =>
          a.name.localeCompare(b.name, undefined, { numeric: true })
        )
    },
    async handleCreatePermissions() {
      this.roleData.name.trim()
      this.isLoadingUpdate = true
      let permissionsIds = this.getPermissionIds()
      try {
        let ret = await organizationApi.createRole({
          name: this.roleData.name,
          description: this.roleData.description,
          permissions: permissionsIds,
        })
        let createdRole = { ...ret.data, custom: true }
        this.roles.push(createdRole)
        this.$root.$emit('managementCreateRole', createdRole)
        this.isLoadingUpdate = false
        this.orderRolesByName()
        this.backToList()
      } catch (e) {
        this.errors.errMsg = e.response.data.errMsg
        this.errors.unknownError = true
        this.isLoadingUpdate = false
        console.error(e)
      }
    },
    async handleUpdateRole() {
      this.roleData.name.trim()
      this.isLoadingUpdate = true
      let permissionsIds = this.getPermissionIds()
      try {
        let ret = await organizationApi.updateRole(this.editingRoleId, {
          name: this.roleData.name,
          description: this.roleData.description,
          permissions: permissionsIds,
        })
        let updatedRole = { ...ret.data, custom: true }
        this.roles = this.roles.map((role) =>
          role.id == updatedRole.id ? updatedRole : role
        )
        this.$root.$emit('updateRoles', updatedRole)
        this.isLoadingUpdate = false
        this.orderRolesByName()
        this.backToList()
      } catch (e) {
        this.isLoadingUpdate = false
        console.error(e)
      }
    },
    async handleDeleteRole(deletingRole) {
      let users = JSON.parse(JSON.stringify(this.users))
      let usersAlreadyWithRole = users.filter((user) => {
        return user.role_id === deletingRole.id
      })
      if (usersAlreadyWithRole.length) {
        this.$root.$emit('openWarningDeleteRole', usersAlreadyWithRole)
        return
      }
      this.isRemoving = true
      try {
        await organizationApi.deleteRole(deletingRole.id)
        this.roles = this.roles.filter((role) => role.id != deletingRole.id)
        this.isRemoving = false
        this.$root.$emit('managementRemoveRole', deletingRole.id)
      } catch (e) {
        this.isRemoving = false
        console.error(e)
      }
    },
    getPermissionIds() {
      let chosenPermissions = []
      this.permissionsTree.forEach((permission) => {
        if (permission.switch === 'read') {
          chosenPermissions.push(...permission.read)
        }
        if (permission.switch === 'editor') {
          chosenPermissions.push(...permission.editor)
        }
        if (permission.switch === 'plan') {
          chosenPermissions.push(...permission.plan)
        }
      })
      let permissionsIds = []
      chosenPermissions.forEach((permission) =>
        permissionsIds.push(permission.id)
      )
      return permissionsIds
    },
    handleClose() {
      this.open = false
    },
    handleMoreDetails() {
      this.moreDetails = true
    },
    backToList() {
      this.editingRoleId = ''
      this.roleData.name = ''
      this.permissionsTree = JSON.parse(
        JSON.stringify(this.defaultPermissionsTree)
      )
      this.isCreating = false
      this.isEditing = false
    },
    handleCreatePermission() {
      this.isCreating = true
    },
    handleEditRole(role) {
      const permissions = JSON.parse(JSON.stringify(role.permissions))
      const permissionsMap = permissions.reduce((map, permission) => {
        if (!map[permission.group]) {
          map[permission.group] = {
            name: permission.group,
            read: false,
            editor: false,
          }
        }
        if (permission.name.includes('get')) {
          map[permission.group].read = true
        }
        if (permission.name.includes('put')) {
          map[permission.group].editor = true
        }
        return map
      }, {})
      let mapArray = Object.values(permissionsMap)
      mapArray.forEach((mappedPermission) => {
        if (!mappedPermission) return
        const { read, editor } = mappedPermission
        let mappedGroupPermission = this.permissionsTree.find(
          (group) => group.group === mappedPermission.name
        )
        if (!read && !editor) {
          mappedGroupPermission.switch = false
        } else if (editor) {
          mappedGroupPermission.switch = 'editor'
        } else {
          mappedGroupPermission.switch = 'read'
        }
      })
      this.roleData.name = role.name
      this.roleData.description = role.description ?? ''
      this.editingRoleId = role.id
      this.isEditing = true
    },
    initialState() {
      return {
        open: false,
        roleData: {
          name: '',
          description: '',
          permissions: [],
        },
        users: [],
        roles: [],
        permissionsTree: [],
        defaultPermissionsTree: [],
        isCreating: false,
        isEditing: false,
        editingRoleId: '',
        isLoadingUpdate: false,
        isRemoving: false,
        errors: {
          unknownError: false,
          errMsg: '',
        },
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.title {
  font-weight: 500;
  font-size: 18px;
}
.userName {
  font-weight: 500;
  font-size: 20px;
  line-height: 120%;
}
.userEmail {
  font-weight: 400;
  font-size: 16px;
  line-height: 150%;
}
.userRole {
  font-weight: 500;
  font-size: 14px;
  line-height: 150%;
}
.rowTitle {
  font-weight: 500;
  font-size: 16px;
  line-height: 150%;
  color: #373b54;
}
.detailsText {
  font-weight: 400;
  font-size: 12px;
  color: var(--v-brand-darken1);
  cursor: pointer;
}
.roleTitle {
  font-weight: 500;
  font-size: 18px;
  line-height: 120%;
}
.roleSubtitle {
  font-weight: 400;
  font-size: 16px;
  line-height: 150%;
  display: -webkit-box;
  max-width: 440px;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.roleCard {
  border: 1px solid #a0acff !important;
  border-radius: 4px;
}
.permissionFieldTitle {
  font-weight: 500;
  font-size: 16px;
  line-height: 100%;
  color: var(--v-textType);
}

.bg-dark,
.theme--dark {
  .detailsText {
    color: #70a5f2;
  }
}
</style>
